import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { isTokenDue, jwtExpiresAt } from "@dolfin/lib";
import { SESSION_STORAGE_KEY } from "@/lib/constants";
import { api } from "@/api/client";
import {
  readStoredSession,
  sessionRefresher,
  userFromAuthResponse,
  type AuthApiResponse,
  type ClientType,
  type DevPersona,
  type Session,
  type User,
  type UserOrganisation,
} from "./session";

export type { ClientType, DevPersona } from "./session";

export type OAuthProvider = "google" | "email";

interface AuthContextType {
  user: User | null;
  token: string | null;
  activeOrganisationId: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (accessToken: string, provider?: OAuthProvider) => Promise<{ error?: string }>;
  /** Local development only: sign in as a seeded manual-test persona. The backend refuses it everywhere else. */
  devLogin: (persona: DevPersona) => Promise<{ error?: string }>;
  /** The dev-login persona this session belongs to, or null for a real sign-in. */
  devPersona: DevPersona | null;
  register: (accessToken: string, magicLinkToken: string, provider?: OAuthProvider) => Promise<{ error?: string; status?: number }>;
  logout: () => void;
  switchOrganisation: (organisationId: string) => void;
  /** Live/Sandbox type of an organisation's owning client, or null if unknown. */
  getClientType: (organisationId: string | null) => ClientType | null;
  /** True while the one-time /v1/me client-type lookup is in flight. */
  clientTypeLoading: boolean;
  clientTypeError: string | null;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [activeOrganisationId, setActiveOrganisationId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [devPersona, setDevPersona] = useState<DevPersona | null>(null);
  const [clientTypeLoading, setClientTypeLoading] = useState(false);
  const [clientTypeError, setClientTypeError] = useState<string | null>(null);
  const googleAccessTokenRef = useRef<string | null>(null);

  // Mirror the stored session into React state. Runs on mount, whenever this tab's refresher rewrites the
  // session, and when another tab writes it (storage event), so every open tab follows the same session.
  const hydrate = useCallback(() => {
    const session = readStoredSession();
    if (session?.token) {
      setToken(session.token);
      // Older sessions predate `clients`; default it so consumers don't crash.
      setUser({ ...session.user, clients: session.user.clients ?? [] });
      setActiveOrganisationId(session.activeOrganisationId);
      setDevPersona(session.devPersona ?? null);
    } else {
      setToken(null);
      setUser(null);
      setActiveOrganisationId(null);
      setDevPersona(null);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // An expired access token no longer means signed out: the refresh cookie may still be good. A dead cookie
      // makes the refresher clear the session, and hydrate() then lands on signed-out.
      const stored = readStoredSession();
      if (stored?.token && isTokenDue(stored.token)) await sessionRefresher.refresh(stored.token);
      if (cancelled) return;
      hydrate();
      setIsLoading(false);
    })();

    const unsubscribe = sessionRefresher.subscribe(hydrate);
    const onStorage = (e: StorageEvent) => {
      if (e.key === SESSION_STORAGE_KEY) hydrate();
    };
    window.addEventListener("storage", onStorage);
    return () => {
      cancelled = true;
      unsubscribe();
      window.removeEventListener("storage", onStorage);
    };
  }, [hydrate]);

  // Renew a minute before the access token expires, so an open tab never hits the 401 path at all.
  useEffect(() => {
    if (!token) return;
    const exp = jwtExpiresAt(token);
    if (exp === null) return;
    const timer = setTimeout(() => void sessionRefresher.refresh(token), Math.max(exp - 60_000 - Date.now(), 0));
    return () => clearTimeout(timer);
  }, [token]);

  // /auth/login doesn't return the client type, so read it from /v1/me once per unseen org and persist it.
  useEffect(() => {
    if (!token || !user || !activeOrganisationId) return;
    const active = user.organisations.find((o) => o.organisationId === activeOrganisationId);
    if (!active || active.clientType !== undefined) return;

    let cancelled = false;
    setClientTypeLoading(true);
    setClientTypeError(null);
    api
      .GET("/v1/me")
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error || !data) {
          setClientTypeError("Couldn't load organisation client type.");
          return;
        }
        const byId = new Map((data.organisations ?? []).map((o) => [o.id, o]));
        setUser((prev) => {
          if (!prev) return prev;
          const organisations: UserOrganisation[] = prev.organisations.map((o) => {
            const me = byId.get(o.organisationId);
            const raw = me?.clientType;
            const clientType: ClientType | null =
              raw === "Live" || raw === "Sandbox" || raw === "Demo" ? raw : null;
            return { ...o, clientId: me?.clientId ?? o.clientId, clientType };
          });
          const next = { ...prev, organisations };
          const session: Session = { token, user: next, activeOrganisationId, devPersona };
          localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));
          return next;
        });
      })
      .catch(() => {
        if (!cancelled) setClientTypeError("Couldn't load organisation client type.");
      })
      .finally(() => {
        if (!cancelled) setClientTypeLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [token, user, activeOrganisationId, devPersona]);

  const getClientType = (organisationId: string | null): ClientType | null => {
    if (!organisationId || !user) return null;
    return user.organisations.find((o) => o.organisationId === organisationId)?.clientType ?? null;
  };

  const saveSession = (authToken: string, authUser: User, orgId: string, persona: DevPersona | null = null) => {
    setToken(authToken);
    setUser(authUser);
    setActiveOrganisationId(orgId);
    setDevPersona(persona);
    const session: Session = { token: authToken, user: authUser, activeOrganisationId: orgId, devPersona: persona };
    localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));
  };

  const login = async (accessToken: string, provider: OAuthProvider = "google"): Promise<{ error?: string }> => {
    const resp = await fetch("/api/proxy/v1/auth/login", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ accessToken, provider }),
    });

    if (!resp.ok) {
      const problem = await resp.json().catch(() => null);
      if (resp.status === 404) {
        return { error: "You don't have an account yet. Contact hello@dolfinai.co for an invite link." };
      }
      return { error: problem?.detail ?? "Login failed. Please try again." };
    }

    const data: AuthApiResponse = await resp.json();
    if (provider === "google") googleAccessTokenRef.current = accessToken;
    const authUser = userFromAuthResponse(data);
    const defaultOrgId = authUser.organisations[0]?.organisationId ?? "";
    saveSession(data.token, authUser, defaultOrgId);
    return {};
  };

  // Dev login returns the same session shape as Google login, so it lands in the same saveSession path.
  const devLogin = async (persona: DevPersona): Promise<{ error?: string }> => {
    const resp = await fetch("/api/proxy/v1/auth/dev-login", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ personaKey: persona.key }),
    });

    if (!resp.ok) {
      const problem = await resp.json().catch(() => null);
      return { error: problem?.detail ?? "Dev login failed." };
    }

    const data: AuthApiResponse = await resp.json();
    const authUser = userFromAuthResponse(data);
    saveSession(data.token, authUser, authUser.organisations[0]?.organisationId ?? "", persona);
    return {};
  };

  const register = async (
    accessToken: string,
    magicLinkToken: string,
    provider: OAuthProvider = "google",
  ): Promise<{ error?: string; status?: number }> => {
    const resp = await fetch("/api/proxy/v1/auth/register", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ accessToken, provider, magicLinkToken }),
    });

    if (!resp.ok) {
      const problem = await resp.json().catch(() => null);
      return { error: problem?.detail ?? "Registration failed. Please try again.", status: resp.status };
    }

    const data: AuthApiResponse = await resp.json();
    if (provider === "google") googleAccessTokenRef.current = accessToken;
    const authUser = userFromAuthResponse(data);

    // The org they just joined via the magic link is the most recently added.
    const activeOrgId = authUser.organisations.at(-1)?.organisationId
      ?? authUser.organisations[0]?.organisationId
      ?? "";
    saveSession(data.token, authUser, activeOrgId);
    return {};
  };

  const switchOrganisation = (organisationId: string) => {
    if (!token || !user) return;
    const exists = user.organisations.some((o) => o.organisationId === organisationId);
    if (!exists) return;
    setActiveOrganisationId(organisationId);
    const session: Session = { token, user, activeOrganisationId: organisationId, devPersona };
    localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));
  };

  const logout = () => {
    void sessionRefresher.logout();
    if (googleAccessTokenRef.current) {
      const g = (window as any).google;
      g?.accounts?.oauth2?.revoke?.(googleAccessTokenRef.current);
      googleAccessTokenRef.current = null;
    }

    setToken(null);
    setUser(null);
    setActiveOrganisationId(null);
    setDevPersona(null);
    localStorage.removeItem(SESSION_STORAGE_KEY);
  };

  return (
    <AuthContext.Provider value={{
      user,
      token,
      activeOrganisationId,
      isAuthenticated: !!token,
      isLoading,
      login,
      devLogin,
      devPersona,
      register,
      logout,
      switchOrganisation,
      getClientType,
      clientTypeLoading,
      clientTypeError,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
}
